import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetNetplusTeamOptionsQuery,
  useGetNetplusTeamsQuery,
} from "../../../store/appApi";
import { selectUserRole } from "../../../store/authSlice";
import {
  createNetplusTeamApi,
  deactivateNetplusTeamApi,
  updateNetplusTeamApi,
} from "../../../services/netplusTeam";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500";

const EMPTY = { _id: "", name: "", managerId: "", memberIds: [], notes: "" };

const TeamForm = ({ show, onHide, initial, options }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setForm(initial || EMPTY);
  }, [initial, show]);

  const managerOptions = (options?.managers || []).map((m) => ({
    value: m._id,
    label: m.name,
  }));
  // Anyone eligible except the chosen boss. A person may be in many teams.
  const memberOptions = (options?.employees || [])
    .filter((e) => e._id !== form.managerId)
    .map((e) => ({
      value: e._id,
      label: `${e.name} — ${e.role}`,
    }));
  const memberValue = memberOptions.filter((o) =>
    form.memberIds.includes(o.value),
  );

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        name: form.name,
        managerId: form.managerId,
        memberIds: form.memberIds,
        notes: form.notes,
      };
      if (form._id) await updateNetplusTeamApi(form._id, payload);
      else await createNetplusTeamApi(payload);
      dispatch(appApi.util.invalidateTags(["NetplusTeams"]));
      onHide();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Unable to save team",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{form._id ? "Edit team" : "New team"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="space-y-4">
          <div>
            <label className={labelClass}>Team name</label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. North Zone Sales"
              required
            />
          </div>
          <div>
            <label className={labelClass}>Team boss (Manager Netplus)</label>
            <Select
              options={managerOptions}
              value={
                managerOptions.find((o) => o.value === form.managerId) || null
              }
              onChange={(o) =>
                setForm({
                  ...form,
                  managerId: o?.value || "",
                  memberIds: form.memberIds.filter((id) => id !== o?.value),
                })
              }
              placeholder="Search manager…"
              isClearable
            />
          </div>
          <div>
            <label className={labelClass}>
              Team members ({form.memberIds.length})
            </label>
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={memberOptions}
              value={memberValue}
              onChange={(list) =>
                setForm({
                  ...form,
                  memberIds: (list || []).map((o) => o.value),
                })
              }
              placeholder="Search and add staff…"
            />
            <p className="mt-1 text-xs text-gray-400">
              A person can belong to more than one team, and a manager can be a
              member of another manager's team.
            </p>
          </div>
          <div>
            <label className={labelClass}>Notes (optional)</label>
            <textarea
              className={inputClass}
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm"
            onClick={onHide}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save team"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

const Teams = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const { data, isFetching } = useGetNetplusTeamsQuery({});
  const { data: options } = useGetNetplusTeamOptionsQuery(undefined, {
    skip: !isSuperAdmin,
  });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const teams = useMemo(() => data?.teams || [], [data]);

  const openNew = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (team) => {
    setEditing({
      _id: team._id,
      name: team.name,
      managerId: team.manager?._id || "",
      memberIds: (team.members || []).map((m) => m._id),
      notes: team.notes || "",
    });
    setShowForm(true);
  };
  const deactivate = async (team) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: `Deactivate "${team.name}"?`,
      text: "The manager stops seeing these members in Netplus Reports. Existing reports are kept.",
      showCancelButton: true,
      confirmButtonText: "Deactivate",
    });
    if (!ok.isConfirmed) return;
    try {
      await deactivateNetplusTeamApi(team._id);
      dispatch(appApi.util.invalidateTags(["NetplusTeams"]));
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  return (
    <>
      <PageTitle
        activeMenu={isSuperAdmin ? "Team Netplus" : "My Team"}
        motherMenu="Workforce"
      />
      <div className="space-y-6">
        <section className={`${panel} p-6`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Team Netplus
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {isSuperAdmin
                  ? "Create teams from the employee list under a Manager Netplus."
                  : "Your team members. Their combined daily report is in Netplus Reports."}
              </p>
            </div>
            <div className="flex gap-2">
              {/* <button
                type="button"
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:border-amber-400"
                onClick={() => navigate("/netplus-team/reports")}
              >
                Netplus Reports
              </button> */}
              {isSuperAdmin && (
                <button
                  type="button"
                  className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400"
                  onClick={openNew}
                >
                  + New team
                </button>
              )}
            </div>
          </div>
        </section>

        {isFetching && !teams.length ? (
          <section className={`${panel} p-10 text-center text-gray-400`}>
            Loading…
          </section>
        ) : !teams.length ? (
          <section className={`${panel} p-10 text-center text-gray-400`}>
            {isSuperAdmin
              ? "No teams yet. Create the first one."
              : "You have not been assigned a team yet."}
          </section>
        ) : (
          teams.map((team) => (
            <section key={team._id} className={panel}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {team.name}
                    {!team.active && (
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-500">
                        INACTIVE
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Manager: <b>{team.manager?.name || "—"}</b> ·{" "}
                    {team.memberCount} member{team.memberCount === 1 ? "" : "s"}
                  </div>
                </div>
                {isSuperAdmin && team.active && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:border-amber-400"
                      onClick={() => openEdit(team)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-red-200 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      onClick={() => deactivate(team)}
                    >
                      Deactivate
                    </button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Member</th>
                      <th className="px-4 py-2 text-left">Phone</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(team.members || []).map((m) => (
                      <tr key={m._id}>
                        <td className="px-4 py-2 font-medium text-gray-800">
                          {m.name}
                          {m.status === false && (
                            <span className="ml-2 text-xs text-red-500">
                              (inactive)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {m.phone || "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:border-amber-400"
                              onClick={() =>
                                navigate(`/staff-reporting/employee/${m._id}`)
                              }
                            >
                              Staff reports
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}
      </div>

      {isSuperAdmin && (
        <TeamForm
          show={showForm}
          onHide={() => setShowForm(false)}
          initial={editing}
          options={options}
        />
      )}
    </>
  );
};

export default Teams;
